import { Dispatch } from "redux";
import {
  getActiveDivisions,
  createDivision,
  updateDivision,
  DivisionReportTextDto,
  normalizeDivisionReportTextDto,
} from "app/shared/api/getDivision";
import { getDivisionById } from "app/shared/api/getDivisionById";
import {
  getBookingSystemProductExclusions,
  BookingSystemExclusionSummary,
} from "app/shared/api/bookingSystemExclusions";
import { createPricingYear } from "app/shared/api/getYears";


// ---------- Types ----------
export type Division = {
  id: number;
  name: string;
  termsAndConditions?: string;
  groupsPaymentTerms?: string;
  websiteUrl?: string;
  address?: {
    id?: number;
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    countryISOCode?: string;
    email?: string;
    telephone?: string;
  } | null;
  accreditationBanner?: {
    image?: string;
    contentType?: string;
    fileName?: string;
  } | null;
  isActive: boolean;
  excludedBookingSystemIds?: string[];
  visaLetterNote?: string | null;
  divisionReportTexts?: DivisionReportTextDto[];
  centreId?: number;
  centre?: { id: number };
};

export type SaveDivisionParams =
  // CREATE
  | {
      id?: undefined;
      name: string;
      termsAndConditions?: string;
      groupsPaymentTerms?: string;
      websiteUrl: string;
      address?: Division['address'];
      accreditationBanner?: Division['accreditationBanner'];
      isActive?: boolean;
      visaLetterNote?: string | null;
      visaLetterNoteFormat?: number;
      headOfficeEmailAddress?: string;
      headOfficeTelephoneNo?: string;
      divisionReportTexts?: DivisionReportTextDto[];
      pricingYear?: number;
    }
  // UPDATE
  | {
      id: number;
      name: string;
      termsAndConditions?: string;
      groupsPaymentTerms?: string;
      websiteUrl: string;
      address?: Division['address'];
      accreditationBanner?: Division['accreditationBanner'];
      isActive: boolean;
      visaLetterNote?: string | null;
      visaLetterNoteFormat?: number;
      headOfficeEmailAddress?: string;
      headOfficeTelephoneNo?: string;
      divisionReportTexts?: DivisionReportTextDto[];
    };

// ---------- Action types ----------
export const FETCH_DIVISIONS = "DIVISION_MANAGER/FETCH_DIVISIONS";
export const SET_DIVISIONS = "DIVISION_MANAGER/SET_DIVISIONS";
export const SAVE_DIVISION_REQUEST = "DIVISION_MANAGER/SAVE_DIVISION_REQUEST";
export const SAVE_DIVISION_SUCCESS = "DIVISION_MANAGER/SAVE_DIVISION_SUCCESS";
export const SAVE_DIVISION_FAILURE = "DIVISION_MANAGER/SAVE_DIVISION_FAILURE";
export const DIVISION_SET_FILTER = "DIVISION_MANAGER/SET_FILTER";
export const DIVISION_MODAL_OPEN = "DIVISION_MANAGER/MODAL_OPEN";
export const DIVISION_MODAL_CLOSE = "DIVISION_MANAGER/MODAL_CLOSE";

// ---------- Action creators ----------
export const setDivisions = (items: Division[]) => ({ type: SET_DIVISIONS, payload: items });
export const setFilter = (value: string) => ({ type: DIVISION_SET_FILTER, payload: value });
export const openModal = (editing: Division | null) => ({ type: DIVISION_MODAL_OPEN, payload: editing });
export const closeModal = () => ({ type: DIVISION_MODAL_CLOSE });

// ---------- Helpers ----------
const mapApiDivision = (d: any): Division => ({
  id: d.id,
  name: d.name ?? d.divisionName ?? "",
  termsAndConditions: d.termsAndConditions,
  groupsPaymentTerms: d.groupsPaymentTerms,
  websiteUrl: d.websiteUrl ?? d.websiteLink ?? d.website,
  address: d.address
    ? {
        id: d.address.id,
        line1: d.address.line1,
        line2: d.address.line2,
        line3: d.address.line3,
        line4: d.address.line4,
        countryISOCode: d.address.countryISOCode,
        email: d.address.email,
        telephone: d.address.telephone,
      }
    : null,
  accreditationBanner: d.accreditationBanner
    ? {
        image: d.accreditationBanner.image,
        contentType: d.accreditationBanner.contentType,
        fileName: d.accreditationBanner.fileName,
      }
    : null,
  isActive: typeof d.isActive === "boolean" ? d.isActive : !!d.active,
  excludedBookingSystemIds: Array.isArray(d.excludedBookingSystemIds)
    ? d.excludedBookingSystemIds.map((id: number | string) => String(id))
    : [],
  visaLetterNote: d.visaLetterNote ?? d.visaNotes ?? null,
  divisionReportTexts: Array.isArray(d.divisionReportTexts)
    ? d.divisionReportTexts.map(normalizeDivisionReportTextDto)
    : undefined,
  centreId: d.centreId ?? d.centre?.id,
  centre: d.centre,
} as any);

const mergeDivisionExclusions = (
  divisions: Division[],
  summaries: BookingSystemExclusionSummary[]
): Division[] => {
  if (!Array.isArray(divisions) || divisions.length === 0) {
    return divisions;
  }

  const lookup = new Map<string, string[]>();

  summaries.forEach(summary => {
    const bookingSystemId = String(summary.bookingSystemId);
    const divisionsExcluded = Array.isArray(summary.divisionsExcluded)
      ? summary.divisionsExcluded
      : [];

    divisionsExcluded.forEach(div => {
      const divisionId = div?.divisionId ?? div?.divisionId ?? div;
      if (divisionId === undefined || divisionId === null) {
        return;
      }
      const key = String(divisionId);
      if (!lookup.has(key)) {
        lookup.set(key, []);
      }
      lookup.get(key)!.push(bookingSystemId);
    });
  });

  return divisions.map(division => {
    const key = String(division.id);
    return {
      ...division,
      excludedBookingSystemIds: lookup.get(key) ?? [],
    };
  });
};

const extractErrorMessage = (body: any, fallback: string) => {
  const arr = body?.errors ?? body?.Errors;
  const joined =
    Array.isArray(arr) ? arr.map((e: any) => (typeof e === "string" ? e : e?.message)).filter(Boolean).join(", ") : "";
  return body?.message || body?.Message || joined || fallback;
};

// ---------- Thunks ----------
export const fetchDivisions = () => async (dispatch: Dispatch) => {
  dispatch({ type: FETCH_DIVISIONS });
  try {
    const list = await getActiveDivisions();
    const mapped = Array.isArray(list) ? list.map(mapApiDivision) : [];
    let merged = mapped;
    try {
      const summaries = await getBookingSystemProductExclusions();
      merged = mergeDivisionExclusions(mapped, summaries);
    } catch (err) {
      console.warn("Failed to fetch booking system exclusions for divisions", err);
      merged = mapped;
    }
    dispatch(setDivisions(merged));
  } catch {
    dispatch(setDivisions([]));
  }
};

export const fetchDivisionByIdThunk =
  (id: number) => async (): Promise<Division | null> => {
    try {
      const d = await getDivisionById(id);
      if (!d) {
        return null;
      }
      const mapped = mapApiDivision(d);
      try {
        const summaries = await getBookingSystemProductExclusions();
        const merged = mergeDivisionExclusions([mapped], summaries);
        return merged[0] ?? mapped;
      } catch (err) {
        return mapped;
      }
    } catch (error) {
      return null;
    }
  };

export const saveDivision =
  (payload: SaveDivisionParams) =>
  async (dispatch: Dispatch): Promise<{ ok: boolean; message?: string; id?: number }> => {
    dispatch({ type: SAVE_DIVISION_REQUEST });
    try {
      let res: Response;

      if ("id" in payload && payload.id) {
        const { id, name, termsAndConditions, groupsPaymentTerms, websiteUrl, address, accreditationBanner, isActive, visaLetterNote, visaLetterNoteFormat, headOfficeEmailAddress, headOfficeTelephoneNo, divisionReportTexts } = payload;
        res = await updateDivision(id, { 
          name, 
          termsAndConditions, 
          groupsPaymentTerms, 
          websiteUrl, 
          address, 
          accreditationBanner, 
          isActive, 
          visaLetterNote,
          visaLetterNoteFormat,
          headOfficeEmailAddress,
          headOfficeTelephoneNo,
          divisionReportTexts,
        });
      } else {
        const { name, termsAndConditions, groupsPaymentTerms, websiteUrl, address, accreditationBanner, isActive, visaLetterNote, visaLetterNoteFormat, headOfficeEmailAddress, headOfficeTelephoneNo, divisionReportTexts } = payload;
        res = await createDivision({ 
          name, 
          termsAndConditions, 
          groupsPaymentTerms, 
          websiteUrl, 
          address, 
          accreditationBanner, 
          isActive, 
          visaLetterNote,
          visaLetterNoteFormat,
          headOfficeEmailAddress,
          headOfficeTelephoneNo,
          divisionReportTexts,
        });
      }

      if (!res) {
        dispatch({ type: SAVE_DIVISION_FAILURE, error: "No response" });
        return { ok: false, message: "No response from API" };
      }

      if (!res.ok && res.status !== 400) {
        dispatch({ type: SAVE_DIVISION_FAILURE, error: "Failed" });
        return { ok: false, message: "Failed to save division" };
      }

      const body = await res.json().catch(() => ({}));
      const hasValidationErrors = body?.errors || body?.Errors;

      if (res.ok && !hasValidationErrors) {
        const resultId =
          typeof body?.id === "number"
            ? body.id
            : typeof body?.divisionId === "number"
              ? body.divisionId
              : typeof body?.DivisionId === "number"
                ? body.DivisionId
                : typeof body === "number"
                  ? body
                  : undefined;
        dispatch({ type: SAVE_DIVISION_SUCCESS });
        await (fetchDivisions() as any)(dispatch);
        
        // Create pricing year for new divisions
        if (!("id" in payload && payload.id) && resultId) {
          const pricingYear = "pricingYear" in payload ? payload.pricingYear : undefined;
          if (pricingYear) {
            try {
              await createPricingYear(resultId, pricingYear);
            } catch (pricingYearError) {
              console.warn("Failed to create pricing year for new division:", pricingYearError);
            }
          }
        }
        
        return { ok: true, id: resultId };
      }

      const msg = extractErrorMessage(body, "Validation error");
      dispatch({ type: SAVE_DIVISION_FAILURE, error: msg });
      return { ok: false, message: msg };
    } catch (e: any) {
      const msg = e?.message || "Failed to save division";
      dispatch({ type: SAVE_DIVISION_FAILURE, error: msg });
      return { ok: false, message: msg };
    }
  };